<template>
  <b-modal
    id="detail-vehicle-delivery-modal"
    ref="detail-vehicle-delivery-modal"
    class="mc-modal"
    size="xl"
    centered
    hide-header-close
    :title="title()"
    @show="handleShow"
  >
    <div class="card-body">
      <!-- Header info kendaraan -->
      <div v-if="!isSetDeliverySequence" class="d-flex justify-content-between align-items-center">
        <div class="d-flex">
          <div class="me-3">
            <strong>{{ $t('global.singleFleet') }}</strong>
            <div>
              {{ vehicle?.license_plate }}
              <McBadge
                v-if="vehicle?.hull_no"
                :text="vehicle.hull_no"
                variant="outlinePlainSecondary"
                size="medium"
                type="button"
              />
            </div>
          </div>
          <div v-if="isAddDelivery" class="me-3">
            <strong>{{ $t('routeOptimization.fields.productCategory') }}</strong>
            <div>
              <McChipList
                v-if="vehicle?.product_category"
                :items="[vehicle.product_category]"
                :max-visible="3"
                itemTextField="name"
                showAllMode="dropdown"
                :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
                showAllDropdownTrigger="action"
              />
            </div>
          </div>
        </div>
        <div></div>
      </div>

      <McTable
        v-if="!isAddDelivery && !isSetDeliverySequence"
        :fields="columns"
        :apiClient="apiClient"
        :initialItems="tableData"
        :useClientSide="true"
        :searchable="true"
        :hasCheckbox="false"
        :hasPagination="false"
        :hasLimit="false"
        :isFirstRowClickable="false"
        :tableMaxHeight="'400px'"
        :infiniteScrollInitialItems="10"
        :infiniteScrollBatchSize="10"
        :stickyHeader="true"
      >
        <template #afterSearchBar>
          <McButton
            v-if="isRoSwatActive"
            variant="primary"
            :disabled="disabledAddDelivery()"
            :displayTooltip="disabledAddDelivery()"
            :tooltipText="$t('routeOptimization.tooltip.addDeliveryDisabled')"
            :tooltipPosition="'bottom'"
            :text="$t('routeOptimization.action.addDelivery')"
            @click="handleAddDelivery"
          />
        </template>
        <template #cell(number)="{ value, item }">
          <router-link
            class="text-secondary-route-link"
            :to="{ name: 'Delivery Detail', params: { id: item?.id } }"
          >
            {{ value || '-' }}
          </router-link>
        </template>
        <template #[`cell(status.name)`]="{ value, item }">
          <span
            v-if="item.status?.color"
            class="status-dots_global"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{ value }}</span>
        </template>
        <template #cell(created_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template #cell(pickup)="{ item }">
          <div>
            {{ formatIndexDate(item.pickup?.estimated_arrival_on).date }}
          </div>
          <div class="mc-datetime">
            {{ formatIndexDate(item.pickup?.estimated_arrival_on).time }}
          </div>
        </template>
        <template #cell(dropoff)="{ item }">
          <div>
            {{ formatIndexDate(item.dropoff?.estimated_arrival_on).date }}
          </div>
          <div class="mc-datetime">
            {{ formatIndexDate(item.dropoff?.estimated_arrival_on).time }}
          </div>
        </template>
        <template #cell(product_categories)="{ value }">
          <McChipList
            v-if="value?.length"
            :items="value"
            :max-visible="3"
            itemTextField="name"
            showAllMode="dropdown"
            :showAllDropdownTitle="$t('setting.list.product.field.boxType')"
            showAllDropdownTrigger="action"
          />
        </template>
        <template #cell(total_weight)="{ value }">
          {{ value }}
        </template>
        <template #cell(total_volume)="{ value }">
          {{ value }}
        </template>
      </McTable>

      <b-tabs
        v-else-if="isAddDelivery && !isSetDeliverySequence"
        :key="`select-delivery-${selectDeliveryRenderKey}`"
        v-model="activeDeliveryTab"
        class="mt-3"
        @input="handleDeliveryTabChange"
      >
        <b-tab :title="$t('routeOptimization.label.availableDelivery')" active>
          <ValueHelpTable
            v-show="isTableDataAvailableDelivery"
            ref="available-do-table-ref"
            :isBusy.sync="isAvailableDoBusy"
            :isEditing.sync="isAvailableDoEditing"
            :totalCount.sync="availableDoTotalCount"
            :selectedModel="selectedAvailableDeliveryOrderIds"
            :fullObject="selectedAvailableDeliveryOrders"
            :attrs="availableDoAttrs"
            :multiple="false"
            :listGetter="availableDoListGetter"
            class="mt-3"
            @available-change="handleDataAvailableDelivery"
            @row-selected="handleAvailableRowSelected"
          >
            <template #status="{ value, item }">
              <span
                v-if="item.status?.color"
                class="status-dots_global"
                :style="{ backgroundColor: item.status.color }"
              ></span>
              <span class="mc-text-status">{{ value }}</span>
            </template>
            <template #created_on="{ value }">
              <div>{{ formatIndexDate(value).date }}</div>
              <div class="mc-datetime">
                {{ formatIndexDate(value).time }}
              </div>
            </template>
          </ValueHelpTable>
          <b-card v-show="!isTableDataAvailableDelivery">
            <McEmptyState
              :title="$t('routeOptimization.emptyState.hasNoDeliveryOrder')"
              :desc="$t('routeOptimization.emptyState.hasInvalidProductCategory')"
              :hasButton="false"
            />
          </b-card>
        </b-tab>

        <b-tab :title="$t('routeOptimization.label.unservedDelivery')">
          <ValueHelpTable
            v-show="isTableDataAvailableUnserved"
            ref="unserved-do-table-ref"
            :isBusy.sync="isUnservedDoBusy"
            :isEditing.sync="isUnservedDoEditing"
            :totalCount.sync="unservedDoTotalCount"
            :selectedModel="selectedUnservedDeliveryOrderIds"
            :fullObject="selectedUnservedDeliveryOrders"
            :attrs="unservedDoAttrs"
            :multiple="false"
            :listGetter="unservedDoListGetter"
            class="mt-3"
            @available-change="handleDataAvailableUnserved"
            @row-selected="handleUnservedRowSelected"
          >
            <template #status="{ value, item }">
              <span
                v-if="item.status?.color"
                class="status-dots_global"
                :style="{ backgroundColor: item.status.color }"
              ></span>
              <span class="mc-text-status">{{ value }}</span>
            </template>
            <template #created_on="{ value }">
              <div>{{ formatIndexDate(value).date }}</div>
              <div class="mc-datetime">
                {{ formatIndexDate(value).time }}
              </div>
            </template>
          </ValueHelpTable>
          <b-card v-show="!isTableDataAvailableUnserved">
            <McEmptyState
              :title="$t('routeOptimization.emptyState.hasNoDeliveryOrder')"
              :desc="$t('routeOptimization.emptyState.hasAllocatedOrInvalidDeliveryOrders')"
              :hasButton="false"
            />
          </b-card>
        </b-tab>
      </b-tabs>

      <div v-else-if="!isAddDelivery && isSetDeliverySequence">
        <McAlert
          variant="warning"
          :html="$t('routeOptimization.alert.isOptimizationIgnoredWhenAddingToTrip')"
          withIcon
          :withGap="false"
        />
        <div class="mt-3 d-flex gap-4">
          <div>
            <p class="teal-300 mb-1">
              {{ $t('routeOptimization.fields.licensePlate') }}
            </p>
            <p class="mb-0">
              {{ vehicle?.license_plate }}
              <McBadge
                v-if="vehicle?.hull_no"
                :text="vehicle.hull_no"
                variant="outlinePlainSecondary"
                size="medium"
                type="button"
              />
            </p>
          </div>

          <div>
            <p class="teal-300 mb-1">
              {{ $t('routeOptimization.fields.deliveryNumber') }}
            </p>
            <p class="mb-0">
              {{ selectedDataDelivery?.number }}
            </p>
          </div>
        </div>

        <hr />

        <div>
          <p class="mb-0">{{ $t('routeOptimization.action.selectSequence') }}</p>
          <p class="text-muted">
            {{ $t('routeOptimization.info.willInsertDOAndAutoShiftSequence') }}
          </p>
          <div
            v-for="(item, index) in optionSuggestionSequenceMovement"
            :key="item.sequence"
            :id="`card-${item.sequence}`"
            class="custom-radio-card d-flex align-items-start p-2 mb-2"
            :class="{
              active: selectedMoveOrderToServedVehicle === item.sequence,
              'is-disabled': item.disabled,
            }"
            @click="!item.disabled && (selectedMoveOrderToServedVehicle = item.sequence)"
          >
            <!-- Radio -->
            <b-form-radio
              v-model="selectedMoveOrderToServedVehicle"
              :value="item.sequence"
              :disabled="item.disabled"
              name="move-order-to-served-vehicle"
              class="mx-3 mt-2"
            />

            <!-- Text -->
            <div>
              <p class="p-0 m-0">
                {{
                  (item.can_merge
                    ? $t('routeOptimization.action.mergeTo')
                    : $t('routeOptimization.action.placeInOrder')) +
                  ' #' +
                  item.sequence
                }}
              </p>
              <p class="text-muted p-0 m-0">
                {{
                  index === optionSuggestionSequenceMovement.length - 1
                    ? $t('routeOptimization.action.willPlaceDOAtEndOfSequence')
                    : $t('routeOptimization.action.currentOrder') +
                      ' ' +
                      (item?.address?.name ?? '-')
                }}
              </p>
            </div>
          </div>

          <b-tooltip
            v-for="item in optionSuggestionSequenceMovement"
            :key="'tooltip-' + item.sequence"
            v-if="item.disabled"
            :target="`card-${item.sequence}`"
            triggers="hover"
            placement="bottom"
            boundary="window"
            custom-class="sequence-movement-tooltip"
          >
            {{ $t('routeOptimization.tooltip.isDestinationDuplicateInEarlierSequenceBilingual') }}
          </b-tooltip>
        </div>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <div class="d-flex mx-0 justify-content-end">
        <McButton
          v-if="!isAddDelivery && !isSetDeliverySequence"
          variant="primary"
          :text="$t('global.action.close')"
          @click="handleClose(cancel)"
        />
        <div v-else-if="isAddDelivery && !isSetDeliverySequence">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="handleCancel()"
          />
          <McButton
            variant="primary"
            :text="$t('global.next')"
            :disabled="!hasSelectedDeliveryOrder"
            @click="handleToSelectSequence()"
          />
        </div>
        <div v-else-if="isSetDeliverySequence">
          <McButton
            variant="secondary"
            :text="$t('global.prev')"
            class="me-2"
            @click="handleToSelectDelivery()"
          />
          <McButton
            variant="primary"
            :text="$t('global.action.apply')"
            :disabled="selectedMoveOrderToServedVehicle === null"
            @click="handleMoveDeliveryToServedVehicle()"
          />
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import McBadge from '@/components/McBadge'
import McChipList from '@/components/McChipList'
import McEmptyState from '@/components/McEmptyState'
import McAlert from '@/components/McAlert'
import ValueHelpTable from '@/components/formulate/ValueHelpInputV2/components/ModalBody/Table'

import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

import * as RouteOptimization from '@/services/rest-api/lib/route-optimization/index.js'
import * as Delivery from '@/services/rest-api/lib/route-optimization/delivery-order'
import filterMappings from '../../../lib/constants/filter-mappings'
import { errorUnservedMappings } from '../../../lib/constants/error-unserved-mappings'
import { errorCodeToastMap } from '../../.././lib/constants/error-code-mappings'

export default {
  name: 'DetailVehicleDeliveryModal',
  components: {
    McButton,
    McTable,
    McBadge,
    McChipList,
    McEmptyState,
    McAlert,
    ValueHelpTable,
  },
  mixins: [Helper],
  props: {
    vehicle: {
      type: Object,
      default: () => {},
    },
    settings: {
      type: Object,
      default: () => {},
    },
    containsFleetTask: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableData: [],
      isAddDelivery: false,
      isAvailableDoBusy: false,
      isAvailableDoEditing: false,
      availableDoTotalCount: 0,
      selectedAvailableDeliveryOrderIds: null,
      selectedAvailableDeliveryOrders: {},
      isUnservedDoBusy: false,
      isUnservedDoEditing: false,
      unservedDoTotalCount: 0,
      selectedUnservedDeliveryOrderIds: null,
      selectedUnservedDeliveryOrders: {},
      isTableDataAvailableDelivery: true,
      isTableDataAvailableUnserved: true,
      availableSelectedRowsLength: 0,
      unservedSelectedRowsLength: 0,

      isSetDeliverySequence: false,
      selectedDataDelivery: null,
      activeDeliveryTab: 0,
      selectDeliveryRenderKey: 0,
      optionSuggestionSequenceMovement: [],
      selectedMoveOrderToServedVehicle: null,
      routeOptimizeDeliveryIds: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    hasTmsUserGroupAccess() {
      return this.hasAccessAll(['tms-user-group:read']) && this.isSuperUser()
    },
    resolvedOriginAddressId() {
      if (this.settings?.start_address?.id != null) {
        return this.settings.start_address.id
      }

      return this.vehicle?.timelines?.find((point) => point.type === 'START')?.address?.id ?? null
    },
    resolvedUserGroupId() {
      return this.settings?.user_group?.id ?? null
    },
    columns() {
      return [
        {
          key: 'number',
          label: this.$t('routeOptimization.fields.deliveryNumber'),
          isSortable: true,
        },
        {
          key: 'status.name',
          label: this.$t('routeOptimization.fields.status'),
          isSortable: false,
        },
        {
          key: 'shipment_number',
          label: this.$t('routeOptimization.fields.noShipment'),
          isSortable: false,
          isEllipsis: true,
        },
        {
          key: 'reference_number',
          label: this.$t('routeOptimization.fields.noReference'),
          isSortable: false,
          isEllipsis: true,
        },
        {
          key: 'fleet_task.number',
          label: this.$t('routeOptimization.fields.noFleetTask'),
          isSortable: true,
        },
        {
          key: 'created_on',
          label: this.$t('routeOptimization.fields.createdOn'),
          isSortable: true,
        },
        {
          key: 'pickup',
          label: this.$t('routeOptimization.fields.estimatedPickupOn'),
          isSortable: true,
        },
        {
          key: 'dropoff',
          label: this.$t('routeOptimization.fields.estimatedDeliveredOn'),
          isSortable: true,
        },
        {
          key: 'product_categories',
          label: this.$t('routeOptimization.fields.productCategory'),
          isSortable: false,
        },
        {
          key: 'origin_address.name',
          label: this.$t('routeOptimization.fields.originAddress'),
          isSortable: true,
          isEllipsis: true,
        },
        {
          key: 'destination_address.name',
          label: this.$t('routeOptimization.fields.destinationAddress'),
          isSortable: true,
          isEllipsis: true,
        },
        {
          key: 'customer.name',
          label: this.$t('routeOptimization.fields.shippedCustomerName'),
          isSortable: true,
          isEllipsis: true,
        },
        {
          key: 'billed_customer.name',
          label: this.$t('routeOptimization.fields.billedCustomerName'),
          isSortable: true,
          isEllipsis: true,
        },
        {
          key: 'total_weight',
          label: this.$t('routeOptimization.fields.loadWeight'),
          isSortable: true,
        },
        {
          key: 'total_volume',
          label: this.$t('routeOptimization.fields.loadVolume'),
          isSortable: true,
        },
        {
          key: 'marketing_user.name',
          label: this.$t('routeOptimization.fields.marketingStaff'),
          isSortable: true,
          isEllipsis: true,
        },
      ]
    },
    apiClient() {
      const ro_id = this.$route.params.id
      const rov_id = this.vehicle?.id

      return {
        get: RouteOptimization.getDetailVehicleDelivery(ro_id, rov_id),
      }
    },
    availableDoAttrs() {
      return {
        name: 'available-delivery-order',
        trackBy: 'id',
        tableFields: [
          {
            key: 'number',
            label: this.$t('routeOptimization.fields.deliveryNumber'),
            isSortable: true,
          },
          {
            key: 'status.name',
            label: this.$t('routeOptimization.fields.status'),
            isSortable: false,
            isSlot: true,
            name: 'status',
          },
          {
            key: 'reference_number',
            label: this.$t('routeOptimization.fields.noReference'),
            isSortable: false,
          },
          {
            key: 'destination_address.name',
            label: this.$t('routeOptimization.fields.destinationAddress'),
            isSortable: true,
          },
          {
            key: 'created_on',
            label: this.$t('routeOptimization.fields.createdOn'),
            isSortable: true,
            isSlot: true,
            name: 'created_on',
          },
        ],
      }
    },
    unservedDoAttrs() {
      return {
        name: 'unserved-delivery-order',
        trackBy: 'id',
        tableFields: [
          {
            key: 'number',
            label: this.$t('routeOptimization.fields.deliveryNumber'),
            isSortable: true,
          },
          {
            key: 'status.name',
            label: this.$t('routeOptimization.fields.status'),
            isSortable: false,
            isSlot: true,
            name: 'status',
          },
          {
            key: 'origin_address.name',
            label: this.$t('routeOptimization.fields.destinationAddress'),
            isSortable: true,
          },
          {
            key: 'destination_address.name',
            label: this.$t('routeOptimization.fields.destinationAddress'),
            isSortable: true,
          },
          {
            label: this.$t('routeOptimization.fields.reason'),
            key: 'unserved_reason.raw_type',
            isSticky: true,
            tdAttr: {
              style:
                'right:0;position:sticky;z-index:20;border-left:1px solid #e6e7ec;min-width:150px;color:#f41d00;',
            },
            thAttr: {
              style:
                'top:0;right:0;position:sticky;z-index:30;border-left:1px solid #e6e7ec;min-width:150px;',
            },
            formatter: (value) => this.$t(errorUnservedMappings[value]),
          },
        ],
      }
    },
    hasSelectedDeliveryOrder() {
      return this.availableSelectedRowsLength > 0 || this.unservedSelectedRowsLength > 0
    },
    isRoSwatActive() {
      const isSamator =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive =
        this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false

      return isSamator && isActive
    },
  },
  methods: {
    formatIndexDate,
    resetModalState() {
      this.isAddDelivery = false
      this.isSetDeliverySequence = false
      this.activeDeliveryTab = 0

      this.availableDoTotalCount = 0
      this.unservedDoTotalCount = 0
      this.availableSelectedRowsLength = 0
      this.unservedSelectedRowsLength = 0

      this.selectedAvailableDeliveryOrderIds = null
      this.selectedAvailableDeliveryOrders = {}
      this.selectedUnservedDeliveryOrderIds = null
      this.selectedUnservedDeliveryOrders = {}

      this.selectedDataDelivery = null
      this.optionSuggestionSequenceMovement = []
      this.selectedMoveOrderToServedVehicle = null
    },
    title() {
      if (this.isAddDelivery) {
        return this.$t('routeOptimization.label.selectDelivery')
      }

      if (this.isSetDeliverySequence) {
        return this.$t('routeOptimization.label.setDeliverySequence')
      }

      return this.$t('routeOptimization.label.deliveryDetail')
    },
    availableDoListGetter(filter = {}) {
      const filters = ['status.name in (CONFIRMED RESCHEDULED)', 'vendor.id is null']

      if (this.resolvedOriginAddressId != null) {
        filters.push(`origin_address.id eq ${this.resolvedOriginAddressId}`)
      }

      if (this.hasTmsUserGroupAccess && this.resolvedUserGroupId != null) {
        filters.push(`user_group.id eq ${this.resolvedUserGroupId}`)
      }

      if (
        Array.isArray(this.routeOptimizeDeliveryIds) &&
        this.routeOptimizeDeliveryIds.length > 0
      ) {
        filters.push(`id not_in (${this.routeOptimizeDeliveryIds.join(' ')})`)
      }

      if (this.vehicle?.product_category?.id != null) {
        filters.push(`product_category.id eq ${this.vehicle.product_category.id}`)
      }

      if (Array.isArray(filter.filters) && filter.filters.length > 0) {
        filter.filters.forEach((tableFilter) => {
          const mappedFilter =
            filterMappings[tableFilter.key]?.(tableFilter.key, tableFilter.values) ?? undefined
          if (mappedFilter) {
            filters.push(mappedFilter)
          }
        })
      }

      return Delivery.getIndex(filters)(filter)
    },
    unservedDoListGetter(filter = {}) {
      const filters = ['status.name in (CONFIRMED RESCHEDULED)', 'ro.vehicle_id is null']

      if (this.vehicle?.product_category?.id != null) {
        filters.push(`product_category.id eq ${this.vehicle.product_category.id}`)
      }

      if (Array.isArray(filter.filters) && filter.filters.length > 0) {
        filter.filters.forEach((tableFilter) => {
          if (typeof tableFilter === 'string') {
            filters.push(tableFilter)
            return
          }

          const mappedFilter =
            filterMappings[tableFilter.key]?.(tableFilter.key, tableFilter.values) ?? undefined
          if (mappedFilter) {
            filters.push(mappedFilter)
          }
        })
      }

      const roId = this.$route.params.id
      return RouteOptimization.getSettingDelivery(roId)({
        ...filter,
        filters,
      })
    },
    handleClose(cancel) {
      cancel()
    },
    async handleShow() {
      this.resetModalState()
      try {
        const ro_id = this.$route.params.id
        const rov_id = this.vehicle?.id
        const res = await RouteOptimization.getDetailVehicleDelivery(ro_id, rov_id)({})

        if (res && !res.errors) {
          this.tableData = res.data.paginated_result || []
        }
      } catch (error) {
        console.error('Error loading vehicle delivery details:', error)
      }

      if (this.isRoSwatActive) {
        this.getRouteOptimizeDeliveryIds()
      }
    },
    handleAddDelivery() {
      this.availableDoTotalCount = 0
      this.unservedDoTotalCount = 0
      this.availableSelectedRowsLength = 0
      this.unservedSelectedRowsLength = 0
      this.activeDeliveryTab = 0
      this.isAddDelivery = true
      this.isSetDeliverySequence = false

      this.$nextTick(() => {
        this.$root.$emit('refresh-value-help', 'available-delivery-order')
      })
    },
    handleCancel() {
      this.resetModalState()
    },
    handleDataAvailableDelivery(isAvailable) {
      this.isTableDataAvailableDelivery = isAvailable
    },
    handleDataAvailableUnserved(isAvailable) {
      this.isTableDataAvailableUnserved = isAvailable
    },
    handleAvailableRowSelected(selectedRowsLength) {
      this.availableSelectedRowsLength = selectedRowsLength
      if (selectedRowsLength <= 0) return

      this.$refs['unserved-do-table-ref']?.handleUnselectAll?.()
      this.selectedUnservedDeliveryOrderIds = null
      this.selectedUnservedDeliveryOrders = {}
      this.unservedSelectedRowsLength = 0
    },
    handleUnservedRowSelected(selectedRowsLength) {
      this.unservedSelectedRowsLength = selectedRowsLength
      if (selectedRowsLength <= 0) return

      this.$refs['available-do-table-ref']?.handleUnselectAll?.()
      this.selectedAvailableDeliveryOrderIds = null
      this.selectedAvailableDeliveryOrders = {}
      this.availableSelectedRowsLength = 0
    },
    getSelectedDataFromTableRef(refName) {
      const tableRef = this.$refs[refName]
      if (!tableRef?.HandleApply) {
        return {
          selectedId: null,
          selectedOrder: {},
        }
      }

      const { selectedValues = {}, selectedObject = {} } = tableRef.HandleApply()
      const selectedIds = Object.keys(selectedValues).filter((key) => selectedValues[key] === true)
      const selectedId = selectedIds[0] ?? null

      return {
        selectedId,
        selectedOrder: selectedId ? selectedObject[selectedId] || {} : {},
      }
    },
    async handleToSelectSequence() {
      const availableSelection = this.getSelectedDataFromTableRef('available-do-table-ref')
      const unservedSelection = this.getSelectedDataFromTableRef('unserved-do-table-ref')

      this.selectedAvailableDeliveryOrderIds = availableSelection.selectedId
      this.selectedAvailableDeliveryOrders = availableSelection.selectedOrder
      this.selectedUnservedDeliveryOrderIds = unservedSelection.selectedId
      this.selectedUnservedDeliveryOrders = unservedSelection.selectedOrder

      const selectedData = availableSelection.selectedId
        ? availableSelection.selectedOrder
        : unservedSelection.selectedOrder

      const ro_id = this.$route.params.id
      const rov_id = this.vehicle?.id
      this.selectedDataDelivery = selectedData

      const res = await RouteOptimization.suggestionSequenceMovement(
        ro_id,
        rov_id,
        this.selectedDataDelivery?.id
      )

      let errorMessage = this.$t('routeOptimization.alert.moveDeliveryToServedVehicleFailed')
      if (res.status === 400) {
        this.$mcToast.error(errorMessage)
        return
      }

      this.optionSuggestionSequenceMovement = (res?.data?.suggestions || []).map((item) => ({
        ...item,
        disabled: item.movement === 'DISABLED',
      }))
      this.selectedMoveOrderToServedVehicle = null

      this.isAddDelivery = false
      this.isSetDeliverySequence = true
    },
    handleToSelectDelivery() {
      this.isAddDelivery = true
      this.isSetDeliverySequence = false
      this.isTableDataAvailableDelivery = true
      this.isTableDataAvailableUnserved = true
      this.selectDeliveryRenderKey += 1

      const tableNameByIndex = {
        0: 'available-delivery-order',
        1: 'unserved-delivery-order',
      }

      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$root.$emit(
            'refresh-value-help',
            tableNameByIndex[this.activeDeliveryTab] || 'available-delivery-order'
          )
        })
      })
    },
    handleDeliveryTabChange(index) {
      const tableNameByIndex = {
        0: 'available-delivery-order',
        1: 'unserved-delivery-order',
      }

      const targetTableName = tableNameByIndex[index]
      if (!targetTableName) return

      this.$nextTick(() => {
        this.$root.$emit('refresh-value-help', targetTableName)
      })
    },
    async handleMoveDeliveryToServedVehicle() {
      const selectedSequence = this.selectedMoveOrderToServedVehicle
      if (selectedSequence === null) return

      const selectedSuggestion = this.optionSuggestionSequenceMovement.find(
        (item) => item.sequence === selectedSequence
      )
      if (!selectedSuggestion) return

      const ro_id = this.$route.params.id
      const rov_id = this.vehicle?.id
      const data = {
        engine: 'ENG-0002',
        route_optimization_vehicle_id: rov_id,
        movement: {
          type: selectedSuggestion.movement,
          route_optimization_point_id: selectedSuggestion.point_uuid,
        },
        delivery_order_id: this.selectedDataDelivery?.id,
      }

      const res = await RouteOptimization.moveDeliveryToServedVehicle(ro_id, data)
      let errorMessage = this.$t('routeOptimization.alert.moveDeliveryToServedVehicleFailed')
      if (res.status === 400) {
        const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
        if (issue) {
          errorMessage = this.$t(errorCodeToastMap[issue.code])
        }
        this.$mcToast.error(errorMessage)
        this.$bvModal.hide(`detail-vehicle-delivery-modal`)
        this.resetModalState()
        return
      }

      this.$bvModal.hide(`detail-vehicle-delivery-modal`)
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: `<strong>${this.selectedDataDelivery?.number}</strong> ${this.$t(
                'routeOptimization.alert.hasBeenAddedToTrip'
              )}`,
            },
          ],
        },
      })

      this.$emit('hasBeenAddedToTrip', ro_id, data, res)
    },
    async getRouteOptimizeDeliveryIds() {
      const ro_id = this.$route.params.id
      const res = await RouteOptimization.getSettingDelivery(ro_id)({})
      this.routeOptimizeDeliveryIds = res?.data?.ids || []
    },
    disabledAddDelivery() {
      return this.vehicle?.is_error || this.containsFleetTask
    },
  },
}
</script>

<style lang="scss" scoped>
.custom-radio-card {
  background-color: $white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.custom-radio-card:hover {
  background-color: #eef1f3;
}

.custom-radio-card.active {
  border: 1px solid #007bff;
  background-color: #e7f1ff;
}

.custom-radio-card.is-disabled {
  background-color: #e7ecf4;
  opacity: 0.6;
  cursor: not-allowed;
}

:deep(.sequence-movement-tooltip .tooltip-inner) {
  max-width: 300px;
  white-space: pre-line;
  text-align: left;
}
</style>
